import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { useFocusTrap } from '../hooks/useFocusManagement';

type Step = 'welcome' | 'profile' | 'level' | 'action';

const STEPS: Step[] = ['welcome', 'profile', 'level', 'action'];

const LEVEL_VALUES = [
  { value: 'beginner', labelKey: 'components.onboarding.levels.beginnerLabel', descKey: 'components.onboarding.levels.beginnerDesc' },
  { value: 'intermediate', labelKey: 'components.onboarding.levels.intermediateLabel', descKey: 'components.onboarding.levels.intermediateDesc' },
  { value: 'advanced', labelKey: 'components.onboarding.levels.advancedLabel', descKey: 'components.onboarding.levels.advancedDesc' },
] as const;

interface Props {
  onComplete: () => void;
}

export default function OnboardingWizard({ onComplete }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, completeOnboarding } = useAuthStore();
  const trapRef = useFocusTrap(true);

  const [stepIdx, setStepIdx] = useState(0);
  const [displayName, setDisplayName] = useState(user?.display_name ?? '');
  const [level, setLevel] = useState('');
  const [saving, setSaving] = useState(false);

  const currentStep = STEPS[stepIdx];

  const handleNext = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx((i) => i + 1);
  };

  const handleBack = () => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await completeOnboarding({});
      onComplete();
    } catch {
      // Silently complete — onboarding is not critical
      onComplete();
    }
  };

  const handleFinish = async (action: 'study' | 'quiz') => {
    setSaving(true);
    try {
      await completeOnboarding({
        display_name: displayName || undefined,
        self_assessed_level: level || undefined,
      });
      onComplete();
      navigate(action === 'study' ? '/study' : '/exam');
    } catch {
      onComplete();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" role="dialog" aria-modal="true" aria-label={t('components.onboarding.ariaLabel')}>
      <div ref={trapRef} className="w-full max-w-lg rounded-3xl border border-slate-200/70 bg-white shadow-2xl overflow-hidden dark:border-white/10 dark:bg-card-dark">
        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100 dark:bg-white/[0.06]">
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${((stepIdx + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        <div className="p-6 sm:p-8">
          {/* Step: Welcome */}
          {currentStep === 'welcome' && (
            <div className="text-center space-y-4">
              <span className="text-5xl" aria-hidden="true">&#9729;&#65039;</span>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{t('components.onboarding.welcomeTitle')}</h2>
              <p
                className="text-slate-600 dark:text-slate-400"
                dangerouslySetInnerHTML={{ __html: t('components.onboarding.welcomeDescription') }}
              />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('components.onboarding.welcomeSubtext')}
              </p>
            </div>
          )}

          {/* Step: Profile */}
          {currentStep === 'profile' && (
            <div className="space-y-4">
              <div className="text-center">
                <span className="text-4xl" aria-hidden="true">&#128100;</span>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mt-2">{t('components.onboarding.profileTitle')}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('components.onboarding.profileSubtext')}</p>
              </div>
              <div>
                <label htmlFor="onboard-name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('components.onboarding.displayNameLabel')}
                </label>
                <input
                  id="onboard-name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={user?.username ?? t('components.onboarding.displayNamePlaceholder')}
                  maxLength={50}
                  className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-[3px] focus:ring-blue-600/15 dark:border-white/10 dark:bg-white/5 dark:text-white"
                />
              </div>
            </div>
          )}

          {/* Step: Level */}
          {currentStep === 'level' && (
            <div className="space-y-4">
              <div className="text-center">
                <span className="text-4xl" aria-hidden="true">&#127919;</span>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mt-2">{t('components.onboarding.levelTitle')}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('components.onboarding.levelSubtext')}</p>
              </div>
              <fieldset>
                <legend className="sr-only">{t('components.onboarding.levelLegend')}</legend>
                <div className="space-y-2">
                  {LEVEL_VALUES.map((l) => (
                    <label
                      key={l.value}
                      className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                        level === l.value
                          ? 'border-blue-600 bg-blue-50/60 ring-1 ring-blue-600 dark:border-blue-500 dark:bg-blue-500/10'
                          : 'border-slate-200/70 bg-slate-50/60 hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10'
                      }`}
                    >
                      <input
                        type="radio"
                        name="level"
                        value={l.value}
                        checked={level === l.value}
                        onChange={(e) => setLevel(e.target.value)}
                        className="mt-1 accent-blue-600"
                      />
                      <div>
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{t(l.labelKey)}</span>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t(l.descKey)}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {/* Step: First Action */}
          {currentStep === 'action' && (
            <div className="text-center space-y-5">
              <span className="text-5xl" aria-hidden="true">&#128640;</span>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('components.onboarding.actionTitle')}</h2>
              <p className="text-slate-600 dark:text-slate-400">
                {t('components.onboarding.actionDescription')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => handleFinish('study')}
                  disabled={saving}
                  className="btn-primary flex-1 py-3"
                >
                  {t('components.onboarding.startStudying')}
                </button>
                <button
                  type="button"
                  onClick={() => handleFinish('quiz')}
                  disabled={saving}
                  className="btn-secondary flex-1 py-3"
                >
                  {t('components.onboarding.takePracticeExam')}
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {currentStep !== 'action' && (
            <div className="mt-8 flex items-center justify-between">
              <button
                type="button"
                onClick={handleSkip}
                disabled={saving}
                className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              >
                {t('components.onboarding.skip')}
              </button>
              <div className="flex gap-2">
                {stepIdx > 0 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="btn-secondary px-4 py-2 text-sm"
                  >
                    {t('components.onboarding.back')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary px-6 py-2 text-sm"
                >
                  {t('components.onboarding.next')}
                </button>
              </div>
            </div>
          )}

          {/* Step indicators */}
          <div className="mt-6 flex justify-center gap-2">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 w-8 rounded-full transition-colors ${
                  i <= stepIdx ? 'bg-blue-600' : 'bg-slate-200 dark:bg-white/[0.08]'
                }`}
                aria-hidden="true"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
